import { useMemo } from "react";
import { TextInputFilter } from "@containers/common/text-input-filter/text-input-filter";
import format from "date-fns/format";
import { DISPLAY_DATE, DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { changeRequestStatuses } from "@constants/change-request-statuses";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { Row } from "@tanstack/react-table";

export const useTableFilters = <T extends object>() => {
    const fuzzyTextFilterFn = (row: Row<T>, columnId: string, filterValue: any) => {
        const value = row.getValue<string>(columnId);
        if (value !== null && typeof value !== "undefined") {
            return value.toLowerCase().includes(filterValue.toLowerCase());
        } else {
            return value !== null;
        }
    };

    fuzzyTextFilterFn.autoRemove = (val: string) => !val;

    const changeRequestStatusFilterFn = (row: Row<T>, columnId: string, filterValue: any) => {
        const value = row.getValue<string>(columnId);
        if (value !== null && typeof value !== "undefined") {
            if (changeRequestStatuses[filterValue.toUpperCase()]) {
                return value.toLowerCase() === filterValue.toLowerCase();
            } else {
                return `${value}`.toLowerCase().includes(filterValue.toLowerCase());
            }
        } else {
            if (ChangeRequestStatusTypes.DEPLOYED.includes(filterValue.toUpperCase()) || filterValue === '--') {
                return value === null;
            }
        }

        return false;
    };

    changeRequestStatusFilterFn.autoremove = (val: string) => !val;

    const dateFilterFn = (row: Row<T>, columnId: string, filterValue: string) => {
        const value = row.getValue<Date | number>(columnId);
        if (value !== null && typeof value !== "undefined") {
            return format(value, DISPLAY_DATE).includes(filterValue);
        } else {
            return value !== null;
        }
    };

    dateFilterFn.autoRemove = (val: string) => !val;

    const dateTimeFilterFn = (row: Row<T>, columnId: string, filterValue: string) => {
        const value = row.getValue<Date | number>(columnId);
        if (value !== null && typeof value !== "undefined") {
            return format(value, DISPLAY_DATE_WITH_TIME).includes(filterValue);
        } else {
            return value !== null;
        }
    };

    dateTimeFilterFn.autoRemove = (val: string) => !val;

    const singleSelectFilterFn = (row: Row<T>, columnId: string, filterValue: string) => {
        if (filterValue.length === 0) {
            return true;
        } else {
            return filterValue === row.getValue(columnId);
        }
    };

    singleSelectFilterFn.autoRemove = (val: string) => !val;


    const multiSelectFilterFn = (row: Row<T>, columnId: string, filterValue: string) => {
        if (filterValue.length === 0) {
            return true;
        } else {
            return filterValue.includes(row.getValue(columnId));
        }
    };

    multiSelectFilterFn.autoRemove = (val: Array<string>) => !val;

    const filterTypes = useMemo(() => ({
        fuzzyText: fuzzyTextFilterFn,
        dateFilter: dateFilterFn,
        dateTimeFilter: dateTimeFilterFn,
        singleSelectFilter: singleSelectFilterFn,
        multiSelectFilter: multiSelectFilterFn,
        changeRequestStatusFilter: changeRequestStatusFilterFn
    }), []);

    const defaultColumn = useMemo(() => ({
        Filter: TextInputFilter
    }), []);

    return { defaultColumn, filterTypes };
};